// takes-sidebar — Task G.4.
//
// Promo facade for the desktop "versions" sidebar (rehearsal panel right
// rail). Renders two fake .take version stubs plus one .new-take-btn
// "＋ New version" CTA — all three open the `versions` promo modal on click.
// On the web this is purely visual; the real version timeline lives in
// Band Way desktop.
//
// CSS at app/renderer/ui/rehearsal/takes-sidebar.css (loaded via the
// @desktop-css Vite alias in main.ts). Classes verified against desktop:
//   .rh-takes        — outer rail container
//   .takes-head      — header strip (h4 + .takes-cnt counter)
//   .takes-body      — scrollable body holding takes + new-take CTA
//   .take            — per-version card
//   .new-take-btn    — dashed "＋ New version" CTA
// The .rh-track-promo modifier (web-only-overrides.css, Task 2.7) is
// applied to all three buttons to dim + block pointer-events when the
// promo flow is active.

import { setState } from '../state.js';

export interface TakesSidebarHandle {
  unmount: () => void;
}

const VERSION_STUB_LABELS: readonly string[] = ['Take 1', 'Take 2'];
const NEW_VERSION_LABEL = '＋ New take';

function openPromo(): void {
  setState({ promoModal: { open: true, feature: 'versions' } });
}

export function mountTakesSidebar(host: Element): TakesSidebarHandle {
  host.textContent = '';

  const root = document.createElement('div');
  root.className = 'rh-takes';

  const head = document.createElement('div');
  head.className = 'takes-head';
  const h4 = document.createElement('h4');
  h4.textContent = '📑 Takes';
  head.appendChild(h4);
  root.appendChild(head);

  const body = document.createElement('div');
  body.className = 'takes-body';

  // "＋ New version" CTA at the top (matches desktop G13-A6 ordering — the
  // new-version button sits above existing takes so it's always reachable
  // without scrolling).
  const newBtn = document.createElement('button');
  newBtn.className = 'new-take-btn rh-track-promo';
  newBtn.type = 'button';
  newBtn.dataset.promo = 'versions';
  newBtn.textContent = NEW_VERSION_LABEL;
  newBtn.addEventListener('click', openPromo);
  body.appendChild(newBtn);

  // Two fake .take stubs below.
  for (const label of VERSION_STUB_LABELS) {
    const btn = document.createElement('button');
    btn.className = 'take rh-track-promo';
    btn.type = 'button';
    btn.dataset.promo = 'versions';
    btn.textContent = label;
    btn.addEventListener('click', openPromo);
    body.appendChild(btn);
  }

  root.appendChild(body);
  host.appendChild(root);

  return {
    unmount: () => {
      host.textContent = '';
    },
  };
}
